import ReactDOM from "react-dom";
import { useState, useEffect, ReactPortal } from "react";
import classnamesBind from "classnames/bind";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { useProduct } from "../../context/product/ProductContext";
import useElementWhenReady from "../../hooks/useElementWhenReady";
import useEventBus from "../../hooks/useEventBus";
import { ProductData } from "../../types/productTypes/product";
import useActiveGalleryIndex from "./useActiveGalleryIndex";
import styles from "./AiImageDisclosure.scss";

const classnames = classnamesBind.bind(styles);

const AI_ASSET_TYPE = "Ai Generated";

const SHORT_LABEL_KEY = "pdp_image_disclaimer_short";
const FULL_LABEL_KEY = "pdp_image_disclaimer_long";

function isActiveImageAiGenerated(
  product: ProductData | null | undefined,
  activeGalleryIndex: number | null,
): boolean {
  if (!product?.images || activeGalleryIndex === null) {
    return false;
  }

  const visibleImages = product.images.filter((image) => image.isVisible);

  if (visibleImages.length === 0) {
    return false;
  }

  const imageIndex =
    (activeGalleryIndex - 1 + visibleImages.length) % visibleImages.length;
  const activeImage = visibleImages[imageIndex];

  return activeImage?.aiAssetType === AI_ASSET_TYPE;
}

const AiImageDisclosure = (): ReactPortal | null => {
  const { features, getTranslation } = useRuntimeConstants() as {
    features?: { aiImageDisclosureEnabled?: boolean };
    getTranslation: (key: string) => string;
  };
  const { product } = useProduct() ?? {};
  const portalElement = useElementWhenReady("#image-overlay-slot");
  const galleryElement = useElementWhenReady(".gallery-images");
  const activeGalleryIndex = useActiveGalleryIndex(galleryElement);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEventBus("videoPlaybackStarted", () => setVideoPlaying(true));
  useEventBus("videoPlaybackStopped", () => setVideoPlaying(false));

  useEffect(() => {
    setIsExpanded(false);
  }, [activeGalleryIndex]);

  if (!features?.aiImageDisclosureEnabled || !portalElement || videoPlaying) {
    return null;
  }

  if (!isActiveImageAiGenerated(product, activeGalleryIndex)) {
    return null;
  }

  const shortLabel = getTranslation(SHORT_LABEL_KEY);
  const fullLabel = getTranslation(FULL_LABEL_KEY);

  const toggleExpanded = () => setIsExpanded((expanded) => !expanded);

  return ReactDOM.createPortal(
    <div
      className={classnames(styles.aiImageDisclosure, {
        [styles.expanded]: isExpanded,
      })}
      data-testid="ai-image-disclosure"
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      onClick={toggleExpanded}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggleExpanded();
        }
      }}
      onBlur={() => setIsExpanded(false)}
    >
      <span aria-hidden="true">
        <span className={styles.label}>{shortLabel}</span>
        <span className={styles.expandedMessage}>{fullLabel}</span>
      </span>
      <span className={styles.visuallyHidden}>{fullLabel}</span>
    </div>,
    portalElement,
  );
};

export default AiImageDisclosure;
